import React, { useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import BankSelectionPayment from '../../components/helpers/BankSelectionPayment';
import { doc, updateDoc, increment, collection, addDoc, serverTimestamp } from 'firebase/firestore';
import { db } from '../../FireBaseConfig';
import toast, { Toaster } from 'react-hot-toast';
import errorMonitoring from '../../utils/errorMonitoring';
import { LoadingSpinner } from '../../components/LoadingSpinner';
import styled from 'styled-components';

const PageContainer = styled.div`
    min-height: 100vh;
    background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    padding: 20px;
`;

const ContentCard = styled.div`
    background: white;
    border-radius: 16px;
    padding: 40px;
    box-shadow: 0 10px 40px rgba(0, 0, 0, 0.1);
    max-width: 600px;
    width: 100%;
`;

const Title = styled.h1`
    font-size: 28px;
    font-weight: 700;
    color: #2d3748;
    margin-bottom: 10px;
    text-align: center;
`;

const Subtitle = styled.p`
    font-size: 16px;
    color: #718096;
    margin-bottom: 30px;
    text-align: center;
`;

const AmountDisplay = styled.div`
    background: #f7fafc;
    border: 2px solid #e2e8f0;
    border-radius: 12px;
    padding: 20px;
    margin-bottom: 30px;
    text-align: center;
`;

const AmountLabel = styled.div`
    font-size: 14px;
    color: #718096;
    margin-bottom: 8px;
    text-transform: uppercase;
    letter-spacing: 0.5px;
`;

const AmountValue = styled.div`
    font-size: 36px;
    font-weight: 700;
    color: #2d3748;
`;

const ErrorMessage = styled.div`
    background: #fed7d7;
    border: 2px solid #fc8181;
    border-radius: 12px;
    padding: 20px;
    margin-bottom: 20px;
    color: #c53030;
    text-align: center;
`;

const BackButton = styled.button`
    background: #e2e8f0;
    color: #2d3748;
    border: none;
    border-radius: 8px;
    padding: 12px 24px;
    font-size: 16px;
    font-weight: 600;
    cursor: pointer;
    transition: all 0.2s;
    margin-top: 20px;
    width: 100%;

    &:hover {
        background: #cbd5e0;
    }
`;

const PaymentFormPage = () => {
    const location = useLocation();
    const navigate = useNavigate();
    const { user, loading } = useAuth();

    // Получаем amount из router state
    const amount = location.state?.amount;

    useEffect(() => {
        // Ждем пока AuthContext загрузится
        if (loading) {
            return;
        }

        // Логируем посещение страницы оплаты
        errorMonitoring.addBreadcrumb({
            category: 'navigation',
            message: 'User navigated to payment page',
            data: { amount, hasUser: !!user }
        });

        // Если пользователь не авторизован, редиректим на login
        if (!user) {
            toast.error('Пожалуйста, войдите в систему для пополнения баланса');
            navigate('/Login', { state: { from: '/PaymentForm' } });
            return;
        }

        // Если amount не передан, редиректим обратно
        if (!amount || amount <= 0) {
            // Не показываем ошибку, просто редиректим
            navigate('/PersonalArea');
        }
    }, [amount, user, loading, navigate]);

    const handlePaymentSuccess = async (result) => {
        try {
            errorMonitoring.addBreadcrumb({
                category: 'payment',
                message: 'Payment successful',
                data: { amount, userId: user.uid }
            });

            // Обновляем баланс пользователя в Firestore
            const balanceRef = doc(db, 'balances', user.uid);
            await updateDoc(balanceRef, {
                balance: increment(amount),
                updatedAt: serverTimestamp()
            });

            // Логируем транзакцию
            await addDoc(collection(db, 'transactions'), {
                userId: user.uid,
                type: 'top_up',
                amount: amount,
                transactionId: result.transactionId || `tx_${Date.now()}`,
                invoiceId: result.invoiceId || `inv_${Date.now()}`,
                status: 'completed',
                description: 'Пополнение баланса',
                createdAt: serverTimestamp()
            });

            toast.success(`Баланс успешно пополнен на ${amount} ₸`);
            navigate('/PaymentSuccess', { state: { amount } });
        } catch (error) {
            console.error('Error updating balance after payment:', error);
            errorMonitoring.captureException(error, {
                action: 'update_balance_after_payment',
                userId: user.uid,
                amount
            });
            toast.error('Оплата прошла успешно, но произошла ошибка при обновлении баланса. Обратитесь в поддержку.');
        }
    };

    const handlePaymentFailure = (error) => {
        console.error('Payment failed:', error);
        errorMonitoring.captureException(error, {
            action: 'payment_failure',
            userId: user?.uid,
            amount
        });
        toast.error('Ошибка при оплате. Попробуйте еще раз.');
    };

    const handleBackClick = () => {
        navigate('/PersonalArea');
    };

    // Показываем загрузку пока проверяется авторизация
    if (loading) {
        return (
            <PageContainer>
                <ContentCard>
                    <LoadingSpinner />
                    <p style={{ textAlign: 'center', marginTop: '20px', color: '#718096' }}>
                        Проверка авторизации...
                    </p>
                </ContentCard>
            </PageContainer>
        );
    }

    // Проверки перед рендером виджета
    if (!user) {
        return (
            <PageContainer>
                <ContentCard>
                    <ErrorMessage>
                        Пожалуйста, войдите в систему для пополнения баланса
                    </ErrorMessage>
                    <BackButton onClick={handleBackClick}>
                        Вернуться в личный кабинет
                    </BackButton>
                </ContentCard>
            </PageContainer>
        );
    }

    if (!amount || amount <= 0 || isNaN(amount)) {
        console.log('Invalid amount check:', { amount, type: typeof amount, isNaN: isNaN(amount) });
        return (
            <PageContainer>
                <ContentCard>
                    <ErrorMessage>
                        Пожалуйста, укажите сумму пополнения
                    </ErrorMessage>
                    <BackButton onClick={handleBackClick}>
                        Вернуться в личный кабинет
                    </BackButton>
                </ContentCard>
            </PageContainer>
        );
    }

    return (
        <PageContainer>
            <Toaster position="top-right" reverseOrder={false} />
            <ContentCard>
                <Title>Пополнение баланса</Title>
                <Subtitle>Выберите удобный способ оплаты</Subtitle>

                <BankSelectionPayment
                    amount={amount}
                    description="Пополнение баланса Eparcel"
                    onSuccess={handlePaymentSuccess}
                    onFailure={handlePaymentFailure}
                />

                <BackButton onClick={handleBackClick}>
                    Отменить
                </BackButton>
            </ContentCard>
        </PageContainer>
    );
};

export default PaymentFormPage;